import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { supabase } from "@/integrations/supabase/client";
import { seoTitle, seoDescription } from "@/lib/seo-meta";

const TITLE = seoTitle("Acesso — Concurso Astor");
const DESC = seoDescription(
  "Entre ou cadastre-se para acompanhar a Concurso Astor. Novos cadastros passam por aprovação antes de liberar o acesso.",
);

export const Route = createFileRoute("/auth")({
  validateSearch: (s: Record<string, unknown>) => ({
    next: typeof s.next === "string" ? s.next : "",
  }),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AuthPage,
});

type Mode = "signin" | "signup";

// Only allow same-origin relative paths in `next` to avoid open redirects.
function safeNext(raw: string): string | null {
  if (!raw) return null;
  if (!raw.startsWith("/") || raw.startsWith("//")) return null;
  return raw;
}

function AuthPage() {
  const navigate = useNavigate();
  const { next } = Route.useSearch();
  const nextPath = safeNext(next);
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        if (nextPath) window.location.href = nextPath;
        else navigate({ to: "/admin" });
      }
    });
  }, [navigate, nextPath]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setInfo(null);
    if (mode === "signin") {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      setLoading(false);
      if (error) {
        setError(error.message);
        return;
      }
      if (nextPath) window.location.href = nextPath;
      else navigate({ to: "/admin" });
    } else {
      const redirectTarget = nextPath
        ? `${window.location.origin}${nextPath}`
        : `${window.location.origin}/admin`;
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: redirectTarget },
      });
      setLoading(false);
      if (error) {
        setError(error.message);
        return;
      }
      // Signed up but has no role yet → admin must approve.
      // Sign out immediately so they don't sit in a "no permission" state.
      if (data.session) await supabase.auth.signOut();
      setInfo(
        "Cadastro enviado! Sua conta ficou pendente de aprovação. Você receberá acesso assim que um administrador liberar.",
      );
      setMode("signin");
      setPassword("");
    }
  }

  return (
    <section className="container-svg py-20">
      <div className="mx-auto max-w-md card-glass">
        <div className="flex gap-2 rounded-lg bg-secondary/60 p-1">
          <button
            type="button"
            onClick={() => { setMode("signin"); setError(null); setInfo(null); }}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-semibold transition ${mode === "signin" ? "bg-primary text-primary-foreground" : "text-foreground"}`}
          >
            Entrar
          </button>
          <button
            type="button"
            onClick={() => { setMode("signup"); setError(null); setInfo(null); }}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-semibold transition ${mode === "signup" ? "bg-primary text-primary-foreground" : "text-foreground"}`}
          >
            Cadastrar
          </button>
        </div>

        <h1 className="mt-6 text-2xl font-bold">
          {mode === "signin" ? "Acesse sua conta" : "Criar cadastro"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {mode === "signin"
            ? "Entre para acessar a área administrativa."
            : "Preencha seus dados. O acesso é liberado após aprovação de um administrador."}
        </p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <div>
            <label htmlFor="email" className="text-sm font-semibold">E-mail</label>
            <input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full rounded border border-border bg-background px-4 py-3 text-sm focus:border-primary focus:outline-none" />
          </div>
          <div>
            <label htmlFor="password" className="text-sm font-semibold">Senha</label>
            <input id="password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded border border-border bg-background px-4 py-3 text-sm focus:border-primary focus:outline-none" />
            {mode === "signup" && (
              <p className="mt-1 text-xs text-muted-foreground">Mínimo de 6 caracteres.</p>
            )}
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          {info && <p className="rounded border border-primary/30 bg-primary/5 p-3 text-sm text-foreground">{info}</p>}
          <button disabled={loading} className="btn-primary w-full">
            {loading
              ? (mode === "signin" ? "Entrando..." : "Enviando...")
              : (mode === "signin" ? "Entrar" : "Solicitar cadastro")}
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          <Link to="/" className="hover:text-primary">← Voltar para a capa</Link>
        </p>
      </div>
    </section>
  );
}
